import React from 'react';
import { Container, Row, Col, Button, Card, Form, Table } from 'react-bootstrap';
import Nav from '../../Components/Nav'
import ModalAlert from '../../Components/ModalAlert'
import API from '../../API'

// Models
import Group from './Models/Group'

// Modals
import RenameGroupModal from './Components/RenameGroupModal'
import DeleteGroupModal from './Components/DeleteGroupModal'
import ManageGroupModal from './Components/ManageGroupModal'

export interface GroupsProps {}

export interface GroupsState { 
    // Show Generic Alert
    showMessage:boolean;
    messageTitle:string;
    messageText:string;

    // Show Groups
    groups:Group[];

    // Add Group
    newGroupName:string;

    // Highlight Group
    selectedGroup:Group | null;

    // Modals:
    showRenameGroupModal:boolean;
    showDeleteGroupModal:boolean;
    showManageGroupModal:boolean;

    editGroupId:string | null;
}

export default class Groups extends React.Component<GroupsProps, GroupsState> {
    constructor(props:GroupsProps) {
        super(props)

        this.state = {

            // Show Generic Alert
            showMessage:false,
            messageTitle:"",
            messageText:"",

            // Show Groups
            groups:[],

            // Add Group
            newGroupName:"",

            // Highlight Group
            selectedGroup:null,

            // Modals:
            showRenameGroupModal:false,
            showDeleteGroupModal:false,
            showManageGroupModal:false,

            editGroupId:null
        }
    }

    componentDidMount(){
        this.fetchGroups()
    }

    fetchGroups(){
        API.groups().then(groups=>{
            this.setState({groups:groups})
        }, error=>{
            this.setState({
                showMessage:true,
                messageTitle:"Error",
                messageText:"Unable to fetch groups, please try again later."
            })
        })
    }

    // Generic Modal Actions
    didPressOk(){
        this.setState({showMessage:false})
    }


    didPressDone(){
        this.setState({
            showMessage:false
        })
    }

    // Add New Group
    didInputNewGroupName(event: React.ChangeEvent<HTMLInputElement>){
        const input = event.target.value
        this.setState({newGroupName:input})
    }

    didPressAddNewGroup(){
        // Add New Group
        const name = this.state.newGroupName

        API.createGroup(name).then(group=>{
            this.setState({newGroupName:""})
            this.fetchGroups()
        },error=>{
            const message = error.response.data.error
            this.setState({
                showMessage:true,
                messageTitle:"Error",
                messageText: message
            })
        })
    }

    // Highlighted Group Actions
    setHighlightedGroup(group:Group){
        this.setState({selectedGroup:group})
    } 
    clearHighlightedGroup(){
        this.setState({selectedGroup:null})
    }

    // Delete Group
    didPressDeleteGroup(){
        const group = this.state.selectedGroup!

        // Delete the group
        this.setState({showDeleteGroupModal:false})

        API.deleteGroup(group.groupId).then(response=>{
            this.fetchGroups()
        }, error=>{
            const message = error.response.data.error
            this.setState({
                showMessage:true,
                messageTitle:"Error",
                messageText: message
            })
        })
    }

    didPressCancelDeleteGroup(){
        this.setState({showDeleteGroupModal:false})
    }
    didPressShowDeleteGroupModal(){
        this.setState({showDeleteGroupModal:true})
    }

    // Rename Group
    didPressRenameGroup(newName:string){
        const group = this.state.selectedGroup!
        this.setState({showRenameGroupModal:false})
        
        API.renameGroup(group.groupId, newName).then(group=>{
            this.fetchGroups()
        }, error=>{
            this.setState({
                showMessage:true,
                messageTitle:"Error",
                messageText:"Unable to rename group. Please make sure the new name is not taken and try again."
            })
        })
    }

    didPressShowRenameGroupModal(){
        this.setState({showRenameGroupModal:true})
    }

    didPressCancelRenameGroup(){
        this.setState({showRenameGroupModal:false})
    }

    // Manage Group
    didPressShowManageGroupModal(){
        this.setState({showManageGroupModal:true})
    }

    didPressDoneManageGroup(){
        this.setState({showManageGroupModal:false})
    }

    render() {
        const GroupActionsView = (props:any) => {
            if(this.state.selectedGroup && this.state.selectedGroup.groupId === props.group.groupId){
                return (
                    <div>
                        <Button variant="primary" onClick={this.didPressShowManageGroupModal.bind(this)}>Manage</Button>
                        <Button variant="warning" style={{marginLeft:10,marginRight:10}} onClick={this.didPressShowRenameGroupModal.bind(this)}>Rename</Button>
                        <Button variant="danger" onClick={this.didPressShowDeleteGroupModal.bind(this)}>Delete</Button>
                    </div>
                )
            } else return (null)
        }

        const RenderModals = (props:any) => {
            if(this.state.selectedGroup){
                return (
                    <div>
                        <RenameGroupModal 
                            show={this.state.showRenameGroupModal}
                            group={this.state.selectedGroup}
                            didPressDone={this.didPressCancelRenameGroup.bind(this)}
                            didPressSave={this.didPressRenameGroup.bind(this)} 
                        />

                        <ManageGroupModal 
                            show={this.state.showManageGroupModal}
                            group={this.state.selectedGroup}
                            didPressDone={this.didPressDoneManageGroup.bind(this)} 
                        />

                        <DeleteGroupModal 
                            show={this.state.showDeleteGroupModal}
                            group={this.state.selectedGroup}
                            didPressDone={this.didPressCancelDeleteGroup.bind(this)}
                            didPressDelete={this.didPressDeleteGroup.bind(this)} 
                        />
                    </div>
                )
            } else {
                return (null)
            }
        }

        return (
            <Container fluid>
                <Row>
                    <Nav />
                    <Col xs={6}>
                        <h3 style={{ paddingTop: 10, paddingBottom: 10 }}>Manage Groups</h3>
                        <Card>
                            <Card.Body>
                                <Form>
                                    <Form.Group>
                                        <Form.Label><strong>Create Group</strong></Form.Label>
                                        <Form.Control type="text" placeholder="Insert group name..." value={this.state.newGroupName} onChange={this.didInputNewGroupName.bind(this)} />
                                        <Form.Text className="text-muted">* Group names must be unique</Form.Text>
                                    </Form.Group>
                                    <Button variant="primary" disabled={this.state.newGroupName.length === 0} onClick={this.didPressAddNewGroup.bind(this)}>Add</Button>
                                </Form>
                            </Card.Body>
                        </Card>
                        <br/>
                        <Card>
                            <Card.Body>
                                <h6><strong>All Groups</strong></h6>
                                <Table bordered hover onMouseLeave={this.clearHighlightedGroup.bind(this)}>
                                    <tbody>
                                    { this.state.groups.map(group=>{
                                        return (
                                            <tr key={group.groupId} onMouseEnter={this.setHighlightedGroup.bind(this, group)}>
                                                <td style={{height:70}}>
                                                    {group.name} <GroupActionsView group={group} /> 
                                                </td>
                                            </tr>
                                        )
                                    }) }
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                        <br/>
                        <ModalAlert 
                            show={this.state.showMessage} 
                            messageTitle={this.state.messageTitle} 
                            messageText={this.state.messageText}   
                            didPressOk={this.didPressOk.bind(this)}
                        />

                        <RenderModals />
                    </Col>
                </Row>
            </Container>
        )
    }
}